import { isWeb } from "@tamagui/constants";
import { getConfig } from "../config.js";
import { isDevTools } from "../constants/isDevTools.js";
import { getVariableValue, isVariable } from "../createVariable.js";
import { expandStyle } from "./expandStyle.js";
import { expandStyles } from "./expandStyles.js";
import { getFontsForLanguage, getVariantExtras } from "./getVariantExtras.js";
import { isObj } from "./isObj.js";
import { mergeProps } from "./mergeProps.js";
const getReturnVariablesAs = (props, state) => {
  return !!props.animation || state.resolveVariablesAs === "value" ? isWeb ? "non-color-value" : "value" : "auto";
};
const createPropMapper = (staticConfig) => {
  const variants = staticConfig.variants || {};
  const defaultProps = mergeProps(staticConfig.defaultProps || {}, {}, false)[0];
  let conf;
  const mapper = (key, value, theme, propsIn, state, languageContext, avoidDefaultProps = false, debug) => {
    conf ||= getConfig();
    const props = state.fallbackProps || propsIn;
    const returnVariablesAs = getReturnVariablesAs(props, state);
    const fontFamily = props[conf.inverseShorthands.fontFamily] || props.fontFamily || defaultProps.fontFamily || propsIn.fontFamily || "$body";
    const variantValue = resolveVariants(
      key,
      value,
      props,
      defaultProps,
      theme,
      variants,
      fontFamily,
      conf,
      returnVariablesAs,
      staticConfig,
      "",
      languageContext,
      avoidDefaultProps,
      debug
    );
    if (variantValue) {
      return variantValue;
    }
    let shouldReturn = value !== void 0 && value !== null;
    if (key in conf.shorthands) {
      shouldReturn = true;
      key = conf.shorthands[key];
    }
    if (value) {
      if (value[0] === "$") {
        value = getToken(
          key,
          value,
          conf,
          theme,
          fontFamily,
          languageContext,
          returnVariablesAs,
          debug
        );
      } else if (isVariable(value)) {
        value = resolveVariableValue(key, value, returnVariablesAs);
      }
    }
    if (shouldReturn) {
      return expandStyle(key, value) || [[key, value]];
    }
  };
  return mapper;
};
const resolveVariants = (key, value, props, defaultProps, theme, variants, fontFamily, conf, returnVariablesAs, staticConfig, parentVariantKey, languageContext, avoidDefaultProps = false, debug) => {
  var _a;
  const variant = variants == null ? void 0 : variants[key];
  if (!variant || value === void 0) {
    return;
  }
  let variantValue = getVariantDefinition(variant, key, value, conf);
  if (!variantValue) {
    if (process.env.NODE_ENV === "development") {
      if ((_a = staticConfig.validStyles) == null ? void 0 : _a[key]) {
        return null;
      }
      if (value === false) {
        return null;
      }
      const name = staticConfig.componentName || "[UnnamedComponent]";
      console.warn(
        `No variant found: ${name} has variant "${key}", but no matching value "${value}"`
      );
    }
    return;
  }
  if (typeof variantValue === "function") {
    const fn = variantValue;
    variantValue = fn(
      value,
      getVariantExtras(props, languageContext, theme, defaultProps, avoidDefaultProps)
    );
  }
  let fontFamilyResult;
  if (isObj(variantValue)) {
    const fontFamilyUpdate = variantValue.fontFamily || variantValue[conf.inverseShorthands.fontFamily];
    if (fontFamilyUpdate) {
      fontFamilyResult = getFontFamilyFromNameOrVariable(fontFamilyUpdate, conf);
    }
    variantValue = resolveTokensAndVariants(
      key,
      variantValue,
      props,
      defaultProps,
      theme,
      variants,
      fontFamilyResult || fontFamily,
      conf,
      returnVariablesAs,
      staticConfig,
      parentVariantKey,
      languageContext,
      avoidDefaultProps,
      debug
    );
  }
  if (variantValue) {
    const next = Object.entries(expandStyles(variantValue));
    if (fontFamilyResult && fontFamilyResult[0] === "$") {
      fontFamilyCache.set(next, getVariableValue(fontFamilyResult));
    }
    return next;
  }
};
function getFontFamilyFromNameOrVariable(input, conf) {
  if (isVariable(input)) {
    const val = variableToFontNameCache.get(input);
    if (val) {
      return val;
    } else {
      for (const key in conf.fontsParsed) {
        const familyVariable = conf.fontsParsed[key].family;
        if (isVariable(familyVariable)) {
          variableToFontNameCache.set(familyVariable, key);
          if (familyVariable === input) {
            return key;
          }
        }
      }
    }
  } else if (typeof input === "string") {
    if ((input == null ? void 0 : input[0]) === "$") {
      return input;
    } else {
      if (process.env.NODE_ENV === "development") {
        console.log("[tamagui] should map back", input);
      }
    }
  }
}
const variableToFontNameCache = /* @__PURE__ */ new WeakMap();
const fontFamilyCache = /* @__PURE__ */ new WeakMap();
const getPropMappedFontFamily = (expanded) => {
  return expanded && fontFamilyCache.get(expanded);
};
const resolveTokensAndVariants = (key, value, props, defaultProps, theme, variants, fontFamily, conf, returnVariablesAs, staticConfig, parentVariantKey, languageContext, avoidDefaultProps, debug) => {
  var _a;
  const res = {};
  for (const rKey in value) {
    const fKey = conf.shorthands[rKey] || rKey;
    const val = value[rKey];
    if (fKey in variants) {
      if (parentVariantKey && parentVariantKey === key) {
        res[fKey] = val;
      } else {
        const variantOut = resolveVariants(
          fKey,
          val,
          props,
          defaultProps,
          theme,
          variants,
          fontFamily,
          conf,
          returnVariablesAs,
          staticConfig,
          key,
          languageContext,
          avoidDefaultProps,
          debug
        );
        const { pressStyle, hoverStyle, focusStyle, enterStyle, exitStyle, ...rest } = Object.fromEntries(variantOut);
        const subs = { pressStyle, hoverStyle, focusStyle, enterStyle, exitStyle };
        Object.assign(res, rest);
        for (const key2 in subs) {
          if (subs[key2]) {
            res[key2] ??= {};
            Object.assign(res[key2], subs[key2]);
          }
        }
      }
      continue;
    }
    if (isVariable(val)) {
      res[fKey] = !isWeb || returnVariablesAs === "value" ? val.val : val.variable;
      continue;
    }
    if (typeof val === "string") {
      const fVal = val[0] === "$" ? getToken(
        fKey,
        val,
        conf,
        theme,
        fontFamily,
        languageContext,
        returnVariablesAs,
        debug
      ) : val;
      res[fKey] = fVal;
      continue;
    }
    if (isObj(val)) {
      res[fKey] ??= {};
      Object.assign(
        res[fKey],
        resolveTokensAndVariants(
          fKey,
          val,
          props,
          defaultProps,
          theme,
          variants,
          fontFamily,
          conf,
          returnVariablesAs,
          staticConfig,
          key,
          languageContext,
          avoidDefaultProps,
          debug
        )
      );
    } else {
      res[fKey] = val;
    }
    if (process.env.NODE_ENV === "development") {
      if (debug) {
        if (((_a = res[fKey]) == null ? void 0 : _a[0]) === "$") {
          console.warn(`\u26A0\uFE0F Missing token in theme ${theme.name}:`, fKey, res[fKey], theme);
        }
      }
    }
  }
  return res;
};
const tokenCats = ["size", "color", "radius", "space", "zIndex"].map((name) => ({
  name,
  spreadName: `...${name}`
}));
function getVariantDefinition(variant, key, value, conf) {
  if (typeof variant === "function") {
    return variant;
  }
  if (variant[value]) {
    return variant[value];
  }
  const { tokensParsed } = conf;
  for (const { name, spreadName } of tokenCats) {
    if (variant[spreadName] && value in tokensParsed[name]) {
      return variant[spreadName];
    }
  }
  let fn;
  const type = typeof value;
  if (type === "number") {
    fn = variant[":number"];
  } else if (type === "string") {
    fn = variant[":string"];
  } else if (value === true || value === false) {
    fn = variant[":boolean"];
  }
  return fn || variant["..."] || variant["...size"];
}
const fontShorthand = {
  fontSize: "size",
  fontWeight: "weight"
};
const getToken = (key, value, conf, theme, fontFamily = "$body", languageContext, resolveAs, debug) => {
  var _a, _b, _c;
  const tokensParsed = conf.tokensParsed;
  const fontsParsed = languageContext ? getFontsForLanguage(conf.fontsParsed, languageContext) : conf.fontsParsed;
  let valOrVar;
  let hasSet = false;
  if (value in theme) {
    valOrVar = theme[value];
    hasSet = true;
  } else {
    switch (key) {
      case "fontFamily":
        valOrVar = ((_a = fontsParsed[value]) == null ? void 0 : _a.family) || value;
        hasSet = true;
        break;
      case "fontSize":
      case "lineHeight":
      case "letterSpacing":
      case "fontWeight":
        valOrVar = ((_c = (_b = fontsParsed[fontFamily]) == null ? void 0 : _b[fontShorthand[key] || key]) == null ? void 0 : _c[value]) || value;
        hasSet = true;
        break;
    }
    for (const cat in tokenCategories) {
      if (key in tokenCategories[cat]) {
        const res = tokensParsed[cat][value];
        if (res) {
          valOrVar = res;
          hasSet = true;
        }
      }
    }
    if (!hasSet) {
      const spaceVar = tokensParsed.space[value];
      if (spaceVar) {
        valOrVar = spaceVar;
        hasSet = true;
      }
    }
  }
  if (process.env.NODE_ENV === "development" && isDevTools && debug === "verbose") {
    console.log("   \uFE52 propMapper getToken", { key, valOrVar, theme, hasSet });
  }
  if (hasSet) {
    return resolveVariableValue(key, valOrVar, resolveAs);
  }
  if (process.env.NODE_ENV === "development") {
    if (value && value[0] === "$") {
      console.warn(
        `\u26A0\uFE0F You passed the value "${value}" to the style property "${key}", but there's no theme or token with the key "${value}". Using theme "${theme.name}".

Set the debug prop to true to see more detailed debug information.`
      );
      if (debug) {
        if (isDevTools) {
          console.log("Looked in:", { theme, tokensParsed, fontsParsed });
        }
      }
      return null;
    }
  }
  return value;
};
function resolveVariableValue(key, valOrVar, resolveAs = "auto") {
  if (isVariable(valOrVar)) {
    if (resolveAs === "variable") {
      return valOrVar;
    }
    if (resolveAs === "non-color-value") {
      if (isWeb) {
        if (key in tokenCategories.color) {
          return valOrVar.variable;
        }
      }
      return valOrVar.val;
    }
    if (!isWeb || resolveAs === "value") {
      return valOrVar.val;
    }
    return valOrVar.variable;
  }
  return valOrVar;
}
const tokenCategories = {
  radius: {
    borderRadius: true,
    borderTopLeftRadius: true,
    borderTopRightRadius: true,
    borderBottomLeftRadius: true,
    borderBottomRightRadius: true
  },
  size: {
    width: true,
    height: true,
    minWidth: true,
    minHeight: true,
    maxWidth: true,
    maxHeight: true
  },
  color: {
    color: true,
    backgroundColor: true,
    borderColor: true,
    borderBottomColor: true,
    borderTopColor: true,
    borderLeftColor: true,
    borderRightColor: true
  }
};
export {
  createPropMapper,
  getFontFamilyFromNameOrVariable,
  getPropMappedFontFamily,
  getReturnVariablesAs
};
//# sourceMappingURL=createPropMapper.js.map
